import { type FC, useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer.tsx';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton.tsx';
import { BarHeader } from 'dibs-elements/exports/BarHeader.tsx';
import { Toast } from 'dibs-toaster/exports/Toast.ts';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { ModalBody } from 'dibs-elements/exports/ModalBody.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import Sparkle from 'dibs-icons/exports/legacy/Sparkle.ts';
import { ethereumPaymentSectionUrl } from 'dibs-crypto-utils/exports/constants.ts';
import { UnsupportedBrowserContent } from 'dibs-crypto-utils/exports/WithWalletConnectionEnsured.ts';
import serverVars from 'server-vars';
import { type EndAuctionModal_viewer$key } from './__generated__/EndAuctionModal_viewer.graphql.ts';

import styles from './EndAuctionModal.scss';

const EndAuctionModalBody: FC<{
    isPending: boolean;
    isBuyerClaiming: boolean;
    closeModal: () => void;
}> = ({ isPending, isBuyerClaiming, closeModal }) => {
    if (isPending) {
        return (
            <FormattedMessage
                id="nft.pdp.endAuctionModal.pendingBody"
                defaultMessage="We are finalizing the transaction. Depending upon your gas fees, this could take up to a few minutes. You do not need to leave this page open for the transfer to complete successfully."
            />
        );
    }

    return isBuyerClaiming ? (
        <>
            <FormattedMessage
                id="nft.pdp.endAuctionModal.buyerSuccessBody"
                defaultMessage="The transfer has been completed, and this item is officially part of {yourCollectionLink}!"
                values={{
                    yourCollectionLink: (
                        <Link href="/nft/profile/">
                            <FormattedMessage
                                id="nft.pdp.endAuctionModal.yourCollectionLink"
                                defaultMessage="your collection"
                            />
                        </Link>
                    ),
                }}
            />
            <Button fullWidth onClick={closeModal} className={styles.button}>
                <FormattedMessage
                    id="nft.pdp.endAuctionModal.viewItem"
                    defaultMessage="View Your Item"
                />
            </Button>
        </>
    ) : (
        <>
            <FormattedMessage
                id="nft.pdp.endAuctionModal.sellerSuccessBody"
                defaultMessage="The transaction has been completed and this item has been transferred to its new owner. Your funds are now available in your account."
            />
            <Button fullWidth href={ethereumPaymentSectionUrl} className={styles.button}>
                <FormattedMessage
                    id="nft.pdp.endAuctionModal.viewAccount"
                    defaultMessage="View Your Account"
                />
            </Button>
        </>
    );
};

const EndAuctionToastBody: FC<{ isPending: boolean; isBuyerClaiming: boolean }> = ({
    isPending,
    isBuyerClaiming,
}) => {
    if (isPending) {
        return (
            <FormattedMessage
                id="nft.pdp.sidebar.transferringTokenBody"
                defaultMessage="We are finalizing the transaction. Depending on your gas fees, this could take up to a few minutes."
            />
        );
    }

    return (
        <span>
            {isBuyerClaiming ? (
                <FormattedMessage
                    id="nft.pdp.endAuctionModal.processedTokenBuyerBody"
                    defaultMessage="The transaction has been processed, and this item is officially part of {yourCollectionLink}!"
                    values={{
                        yourCollectionLink: (
                            <Link href="/nft/profile/">
                                <FormattedMessage
                                    id="nft.pdp.endAuctionModal.toastYourCollectionLink"
                                    defaultMessage="your collection"
                                />
                            </Link>
                        ),
                    }}
                />
            ) : (
                <FormattedMessage
                    id="nft.pdp.endAuctionModal.processedTokenSellerBody"
                    defaultMessage="The transaction has been processed and this item has been transferred to its new owner. Your funds are now available in {yourAccountLink}."
                    values={{
                        yourAccountLink: (
                            <Link href={ethereumPaymentSectionUrl}>
                                <FormattedMessage
                                    id="nft.pdp.endAuctionModal.toastYourAccountLink"
                                    defaultMessage="your account"
                                />
                            </Link>
                        ),
                    }}
                />
            )}
        </span>
    );
};

export type EndAuctionProps = {
    modalOpen: boolean;
    modalType: 'PENDING' | 'SUCCESS' | null;
    closeModal: () => void;
    isBuyerClaiming: boolean;
    viewer: EndAuctionModal_viewer$key;
};

const EndAuctionModal: FC<EndAuctionProps> = ({
    modalOpen,
    modalType,
    closeModal,
    isBuyerClaiming,
    viewer: viewerRef,
}) => {
    const isMobile = serverVars.get('settings.isMobile');
    const mobileUnsupportedBrowser = isMobile && !window.ethereum;

    const [isEndAuctionToastOpen, setIsEndAuctionToastOpen] = useState(false);
    useEffect(() => {
        if (!modalOpen && modalType) {
            setIsEndAuctionToastOpen(true);
        }
    }, [modalOpen, modalType]);

    const viewer = useFragment(
        graphql`
            fragment EndAuctionModal_viewer on Viewer {
                ...UnsupportedBrowserContent_viewer
            }
        `,
        viewerRef
    );

    const isPending = modalType === 'PENDING';
    const isProcessed = modalType === 'SUCCESS';

    return (
        <>
            <ModalContainer isOpen={modalOpen} onClose={closeModal}>
                <ModalCloseButton onClick={closeModal} />
                {!mobileUnsupportedBrowser ? (
                    <>
                        <HeadingLevel>
                            {Header => (
                                <BarHeader
                                    title={
                                        isPending ? (
                                            <div className={styles.pendingHeaderWrapper}>
                                                <Spinner
                                                    size="tiny"
                                                    black
                                                    containerClass={styles.spinner}
                                                />
                                                <Header className={styles.header}>
                                                    <FormattedMessage
                                                        defaultMessage="Transferring token"
                                                        id="nft.pdp.endAuctionModal.header"
                                                    />
                                                </Header>
                                            </div>
                                        ) : (
                                            <div className={styles.successHeaderWrapper}>
                                                <Sparkle className={styles.sparkleIcon} />
                                                <Header className={styles.header}>
                                                    <FormattedMessage
                                                        defaultMessage="Successfully Processed"
                                                        id="nft.pdp.endAuctionModal.processed"
                                                    />
                                                </Header>
                                            </div>
                                        )
                                    }
                                />
                            )}
                        </HeadingLevel>
                        <ModalBody className={styles.bodyText}>
                            <EndAuctionModalBody
                                isPending={isPending}
                                isBuyerClaiming={isBuyerClaiming}
                                closeModal={closeModal}
                            />
                        </ModalBody>
                    </>
                ) : (
                    <UnsupportedBrowserContent isMobile={isMobile} viewer={viewer} />
                )}
            </ModalContainer>
            <Toast
                isVisible={isEndAuctionToastOpen}
                showCloseButton={isProcessed}
                onClose={() => setIsEndAuctionToastOpen(false)}
            >
                <div className={styles.toastWrapper}>
                    <div className={styles.toastHeader}>
                        {isPending ? (
                            <Spinner size="tiny" black />
                        ) : (
                            <Sparkle className={styles.toastSparkle} />
                        )}
                        <strong className={styles.toastHeaderText}>
                            {isPending ? (
                                <FormattedMessage
                                    id="nft.pdp.sidebar.transferringToken"
                                    defaultMessage="Transferring Token"
                                />
                            ) : (
                                <FormattedMessage
                                    id="nft.pdp.sidebar.processedTransfer"
                                    defaultMessage="Successfully Processed"
                                />
                            )}
                        </strong>
                    </div>
                    <EndAuctionToastBody isPending={isPending} isBuyerClaiming={isBuyerClaiming} />
                </div>
            </Toast>
        </>
    );
};

export default EndAuctionModal;
